/**
 * @fileoverview Implements an AI-powered code completion strategy for incremental code changes using Levenshtein similarity and reference tracking. The MulticompleteV4 class processes file diffs, identifies potentially related lines, and uses a language model to suggest context-aware line modifications across a file.
 */

import { Model } from '@iterator/shared-node/ai/llm_types'
import { Llama_33_70B_n1_multiLine_Model } from '@src/ai/models'
import { getReferences, Reference } from '@src/ast'
import { toCoallescedChange } from '@src/coallesce_changes'
import { isDiffWithOnlyOneAdditionOrEdit, pickCursorFromLineDiff } from '@src/position'
import { changesToString, truncateDiffChanges } from '@src/rewrite'
import { requireSettings } from '@src/settings'
import {
  levenshteinSimilarity,
  LineRangeDocument,
  mostSimilarLines,
  SimilarDocument,
} from '@src/similarity'
import { diffLines } from 'diff'
import {
  debug_block,
  diff_block,
  error,
  error_block,
  info,
  info_block,
  print_ai,
} from '@iterator/shared-node/util/logging'
import { OnDiffReceiver, PathDiff, printOnDiff } from '../on_diff'
import { AiStreamer } from '../ai_streamer'

/**
 * Plan:
 * - Find the line that was changed.
 * - Use Levenschteing distance to predict the lines that are most likely to be changed.
 * - Ask the LLM to rewrite the lines.
 * - Apply each change individually to the file and show the diff.
 *
 * - v4.0 - give all the lines and ask LLM to write the entire thing including line numbers, making any changes dictated by the prior change.
 *
 * Notes:
 * - Consider just word matching nearby lines for words that are changed (this would work with comments and not require native module support)
 * - when the change a white space or multiple line change, there is weird behavior.
 * - unexpceted benefit: fixing spelling errors on other lines.
 * - will sometimes halluciate line numbers to change and insert lines or replace lines.
 */
export class MulticompleteV4 implements OnDiffReceiver {
  mostRecentResult: string | undefined = undefined
  private isGenerating = false

  private model = Llama_33_70B_n1_multiLine_Model

  constructor(private quiet: boolean = false) {
    requireSettings([...this.model.getApi().requiredKeys])
  }

  async onDiff(diff: PathDiff) {
    if (this.isGenerating) {
      return
    }
    this.isGenerating = true
    if (!this.quiet) {
      printOnDiff(diff)
    }

    try {
      const startTime = Date.now()
      if (!isDiffWithOnlyOneAdditionOrEdit(diff.changes)) {
        info(`👎 No addition or edit to complete`)
        return
      }
      const cursorPosition = pickCursorFromLineDiff(diff.changes)
      if (!cursorPosition) {
        info(`👎 No cursor position provided`)
        return
      }

      const lineText = diff.after.split('\n')[cursorPosition.row]!
      const similarLines = mostSimilarLines(
        lineText,
        [{ path: diff.path, content: diff.after }],
        levenshteinSimilarity,
      )
        .filter((s) => isLineCloseBy(s.start, cursorPosition.row + 1))
        .slice(0, 5)
      debug_block(
        'Similar lines',
        `${cursorPosition.row + 1}: ${lineText}\n\n` + serializeSimilarLines(similarLines),
      )

      const afterReferences = getReferences(diff.after, cursorPosition.row + 1).references.filter(
        (r) => isLineCloseBy(r.line, cursorPosition.row),
      )
      const beforeReferences = getReferences(diff.before, cursorPosition.row + 1).references.filter(
        (r) => isLineCloseBy(r.line, cursorPosition.row),
      )
      const referenceLines = referencesToLineRangeDocuments(
        diff.after,
        beforeReferences,
        afterReferences,
      )
      debug_block('Reference lines', serializeLineRangeDocuments(referenceLines))

      const linesToCheckForChanges = gatherSimilarAndReferenceLines([
        ...similarLines,
        ...referenceLines,
      ])
      info_block('Lines to check for changes', serializeLineRangeDocuments(linesToCheckForChanges))

      this.mostRecentResult = await v4_0(this.model, diff, linesToCheckForChanges)

      diff_block(
        `multicomplete (${Date.now() - startTime}ms)`,
        diff.after !== this.mostRecentResult
          ? changesToString(truncateDiffChanges(diffLines(diff.after, this.mostRecentResult), 5))
          : 'No changes',
      )
    } catch (e: any) {
      error(e)
      error_block('Error incremental progress', e)
    } finally {
      this.isGenerating = false
    }
  }
}

function referencesToLineRangeDocuments(
  content: string,
  before: Reference[],
  after: Reference[],
): LineRangeDocument[] {
  const lines = content.split('\n')
  const seen = new Set<number>()
  return [...before, ...after]
    .filter((r) => {
      if (seen.has(r.line)) return false
      seen.add(r.line)
      return true
    })
    .map(
      (r) =>
        ({
          // Annoyingly ast is 1-indexed so we'll convert here to match.
          start: r.line,
          end: r.line,
          text: lines[r.line - 1],
        }) as LineRangeDocument,
    )
}

function gatherSimilarAndReferenceLines(documents: LineRangeDocument[]): LineRangeDocument[] {
  // Sort by start line and remove duplicates
  const seen = new Set<number>()
  return documents
    .sort((a, b) => a.start - b.start)
    .filter((doc) => {
      if (seen.has(doc.start)) return false
      seen.add(doc.start)
      return true
    })
}

function serializeSimilarLines(similarLines: SimilarDocument[]): string {
  return similarLines.map((s) => `(${s.similarity.toFixed(4)}) ${s.start}: ${s.text}`).join('\n')
}

function serializeLineRangeDocuments(lineRangeDocuments: LineRangeDocument[]): string {
  return lineRangeDocuments.map((s) => `${s.start}:${s.text}`).join('\n')
}

function isLineCloseBy(row: number, other: number): boolean {
  return Math.abs(row - other) <= 10
}

async function v4_0(model: Model, diff: PathDiff, lines: LineRangeDocument[]): Promise<string> {
  const system = `You are an AI coding assistant.
You are given a line that was edited and a list of lines that might need to be changed as a result of the edit.
Your job is for each line, (1) determine if it needs to be changed, and (2) if so, write the new version of the line.
You rewrite the entire list of lines including line numbers and make any changes dictated by the prior change.
The list of lines has the following format: 
<line number>:<line text>
<line number>:<line text>
<line number>:<line text>
...
`
  const linesPrompt = serializeLineRangeDocuments(lines)

  const api = model.getApi()
  const streamer = new AiStreamer()
  const { message, usage } = await api.streamChatCompletions(
    system,
    [changesToPrompt(diff)],
    [
      {
        role: 'user',
        content: `Rewrite the following lines and make any changes dictated by the prior change. Output the lines with line numbers and nothing else, especially no markdown or other formatting.`,
      },
      {
        role: 'user',
        content: linesPrompt,
      },
    ],
    (chunk) => streamer.stream(chunk),
    linesPrompt,
  )
  print_ai('')

  const lineChanges = parseLineChanges(message.content)

  info_block(`Usage ${model.name}`, {
    ...usage,
    promptCost: usage.promptTokens * model.promptTokenCost,
    completionCost: usage.completionTokens * model.completionTokenCost,
    cost:
      usage.promptTokens * model.promptTokenCost +
      usage.completionTokens * model.completionTokenCost,
  })

  return diff.after
    .split('\n')
    .map((line, index) => {
      const change = lineChanges.find((c) => c.start === index + 1)
      return change ? change.text : line
    })
    .join('\n')
}

function parseLineChanges(message: string): LineRangeDocument[] {
  return message.split('\n').map((line) => {
    const [lineNumber, ...lineTextParts] = line.split(':')
    const lineText = lineTextParts.join(':')
    if (!lineNumber || !lineText) {
      throw new Error(`Invalid line format: ${line}`)
    }
    return { start: parseInt(lineNumber), end: parseInt(lineNumber), text: lineText }
  })
}

function changesToPrompt(diff: PathDiff) {
  const changes = toCoallescedChange(diff.changes)

  const edit = changes.find((c) => c.type === 'edit')
  if (edit) {
    return `${edit.priorValue}\n\nwas edited to:\n\n${edit.value}`
  }

  const add = changes.find((c) => c.type === 'insert')
  if (add) {
    return `The following line was added:\n\n${add.value}`
  }

  const remove = changes.find((c) => c.type === 'remove')
  if (remove) {
    return `The following line was removed:\n\n${remove.value}`
  }

  throw new Error('No edit or add found')
}
